/**
 * PCMCapture — microphone capture that produces raw 16-bit LE PCM at 16 kHz,
 * mono, base64-encoded in chunks. Format compatible with Gemini Live API
 * (mime_type="audio/pcm;rate=16000").
 *
 * Replaces the previous MediaRecorder-based approach (which silently produced
 * WebM/Opus containers that Gemini rejects).
 *
 * Usage:
 *   const cap = new PCMCapture({ onChunk: (b64) => ws.send(b64), onError: console.error });
 *   await cap.start();
 *   // ...
 *   cap.stop();
 */

const TARGET_SAMPLE_RATE = 16000;
const WORKLET_PATH = (process.env.PUBLIC_URL || '') + '/pcm-worklet.js';

export class PCMCapture {
  constructor({ onChunk, onError, onReady } = {}) {
    this.onChunk = onChunk;
    this.onError = onError || ((e) => console.error('[PCMCapture]', e));
    this.onReady = onReady;
    this.audioContext = null;
    this.mediaStream = null;
    this.sourceNode = null;
    this.workletNode = null;
    this.started = false;
  }

  async start() {
    if (this.started) return;
    try {
      // 1. Get mic stream (browser handles device selection & resampling)
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // 2. Create AudioContext at 16 kHz so we don't need to resample in JS.
      // Most browsers (Chrome 74+, Firefox 76+, Safari 15+) honor this.
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      try {
        this.audioContext = new AudioCtx({ sampleRate: TARGET_SAMPLE_RATE });
      } catch (e) {
        // Older browsers may refuse a non-default rate; fall back and warn.
        console.warn(
          '[PCMCapture] Browser refused 16 kHz context; falling back to default rate. ' +
            'Voice quality may degrade on this device.',
          e
        );
        this.audioContext = new AudioCtx();
      }

      // Some browsers start the context suspended until a user gesture.
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      if (this.audioContext.sampleRate !== TARGET_SAMPLE_RATE) {
        // Worklet expects 16 kHz frames; warn loudly if we didn't get it.
        console.warn(
          `[PCMCapture] AudioContext sampleRate is ${this.audioContext.sampleRate}, expected ${TARGET_SAMPLE_RATE}. ` +
            'Gemini Live may reject or mis-interpret audio on this browser.'
        );
      }

      // 3. Register the worklet processor
      await this.audioContext.audioWorklet.addModule(WORKLET_PATH);

      // 4. Wire up: mic -> worklet (no output; worklet posts messages)
      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-capture-processor', {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        channelCount: 1,
      });

      this.workletNode.port.onmessage = (e) => {
        if (!this.onChunk) return;
        try {
          const b64 = arrayBufferToBase64(e.data);
          this.onChunk(b64);
        } catch (err) {
          this.onError(err);
        }
      };

      this.sourceNode.connect(this.workletNode);
      this.started = true;
      this.onReady?.({ sampleRate: this.audioContext.sampleRate });
    } catch (err) {
      this.onError(err);
      await this.stop();
      throw err;
    }
  }

  async stop() {
    try {
      if (this.workletNode) {
        this.workletNode.port.onmessage = null;
        try {
          this.workletNode.disconnect();
        } catch (_) {
          /* ignore */
        }
        this.workletNode = null;
      }
      if (this.sourceNode) {
        try {
          this.sourceNode.disconnect();
        } catch (_) {
          /* ignore */
        }
        this.sourceNode = null;
      }
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((t) => t.stop());
        this.mediaStream = null;
      }
      if (this.audioContext && this.audioContext.state !== 'closed') {
        await this.audioContext.close();
      }
      this.audioContext = null;
    } catch (e) {
      // Swallow cleanup errors; nothing useful to do with them.
      console.warn('[PCMCapture] cleanup warning:', e);
    } finally {
      this.started = false;
    }
  }
}

/**
 * Efficient base64 encoding of a binary ArrayBuffer without hitting the
 * String.fromCharCode apply() argument-limit on large frames.
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const CHUNK = 0x8000; // 32k chars per call — safe on all browsers
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, Math.min(i + CHUNK, bytes.length))
    );
  }
  return btoa(binary);
}

export default PCMCapture;
